// @noflow
import React from 'react';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import ErrorScreen from './app/ErrorScreen';

/**
 * Notify Sentry of a handled error
 * @see ""
 * @param {string|Error} error Error message
 * @param {Object} metaData Metadata
 * @param {string} severity Severity
 * * @param {boolean} isConsecutive Whether or not the error is consecutive
 * * @param {boolean} isSlingStartup Whether or not the error is a sling startup
 * @returns {undefined}
 */
export const notify = ({
  error,
  metaData,
  severity = 'warning',
  isConsecutive = false,
  isSlingStartup = false,
}) => {
  if (isSlingStartup) {
    Sentry.captureException(new Error('slingStartup'), {
      tags: {
        slingStartup: true,
        slingingSession: true,
      },
      contexts: {
        metaData,
      },
      level: Sentry.Severity.Info,
    });
  } else if (error && error.message === 'receiverReboot' && metaData && metaData.source) {
    const { source, isSlingingSession } = metaData;
    delete metaData.source;
    delete metaData.isSlingingSession;
    Sentry.captureException(error, {
      tags: {
        receiverReboot: true,
        rebootSource: source,
        slingingSession: isSlingingSession,
      },
      contexts: {
        metaData,
      },
      level: Sentry.Severity.Info,
    });
  } else if (error && error.message === 'asyncPopup' && metaData && metaData.isSlingingSession) {
    const { isSlingingSession } = metaData;
    delete metaData.isSlingingSession;
    Sentry.captureException(error, {
      tags: {
        slingingSession: isSlingingSession,
      },
      contexts: {
        metaData,
      },
      level: Sentry.Severity.Info,
    });
  } else if (metaData && metaData?.playerError) {
    // Pull out playerError and slinging session keys
    const { playerError, isSlingingSession } = metaData;
    // Delete these keys from the object for spreading anything extra inside metaData into the contexts.
    delete metaData.playerError;
    delete metaData.isSlingingSession;
    const {
      softwareVersion,
      errorCode,
      connectionMode,
      bitmovinErrorCode,
      bitmovinErrorMessage,
      austinVersion,
    } = playerError;

    const { errorObject } = playerError;
    let errorObjectJson;

    if (errorObject) {
      errorObjectJson = JSON.parse(errorObject);
    }

    const { reason, info } = errorObjectJson || {};

    Sentry.captureException(error, {
      tags: {
        errorCode,
        softwareVersion,
        connectionMode,
        bitmovinErrorCode,
        bitmovinErrorMessage,
        consecutive: isConsecutive,
        slingingSession: isSlingingSession,
        austinVersion,
      },
      contexts: {
        playerError,
        metaData,
        errorObject: {
          reason,
          ...info,
        },
      },
    });
  }
};

const beforeSend = (event, store) => {
  const { app, auth, config } = store.getState();

  if (app.activeReceiver) {
    const { finderId, model, nickname, receiverId, status } = app?.activeReceiver;

    event.extra = event.extra || {};
    event.tags = event.tags || {};
    event.user = event.user || {};

    event.extra = {
      ...event.extra,
      activeReceiver: {
        finderId,
        model,
        nickname,
        receiverId,
        status,
      },
    };

    event.tags = {
      ...event.tags,
      receiverId,
      receiverName: nickname,
      finderId,
    };

    event.user = {
      ...event.user,
      // eslint-disable-next-line camelcase
      analytics_id: auth?.viewer?.analytics_id,
    };

    // Send all slinging events to Sentry
    // if (event.tags.slingingSession) {
    return event;
    // }

    // // Only send 10% of normal errors to Sentry
    // const sampleRate = config.reporting.sample_rate;
    // return Math.random() * 100 <= sampleRate ? event : null;
  }

  return null;
};

const configureReporting = store => {
  const { config } = store.getState();

  Sentry.init({
    dsn: config.reporting.sentry_dsn_url,
    release: config.appVersion,
    maxBreadcrumbs: 10,
    maxEvents: 5,
    normalizeDepth: 6,
    environment: config.base.env,
    beforeSend: event => beforeSend(event, store),
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: config.base.env === 'production' ? 0.1 : 1.0, // Be sure to lower this in production
  });

  return ({ children }) => (
    <Sentry.ErrorBoundary fallback={ErrorScreen}>{children}</Sentry.ErrorBoundary>
  );
};

export default configureReporting;
